import type { ColDef } from "ag-grid-community";
import { useMemo } from "react";

import {
	getProductArea,
	type Product,
	type ProductArea,
} from "@/shared/models/Product";
import { withFraction32 } from "@/utils/fraction";

type SpecRow = {
	label: string;
	value: string | number | null | undefined;
	// Inches: also render the value to the nearest 1/32".
	fraction?: boolean;
};

const yesNo = (v: boolean | null | undefined): string | null =>
	v === true ? "Yes" : v === false ? "No" : null;

const bundleSize = (p: Product) =>
	p.BundleCol != null || p.BundleRow != null
		? `${p.BundleCol ?? "-"} x ${p.BundleRow ?? "-"} = ${p.BundleCount ?? "-"}`
		: null;

// FinishSpecs rows — visibility mirrors FinishSpecsFields.tsx exactly.
const finishSpecRows = (
	p: Product,
	area: Exclude<ProductArea, null>,
): SpecRow[] => {
	const isSaw = area === "saw";
	const isWax = area === "wax";
	const isGalvSawed = area === "galv-sawed";
	const rows: SpecRow[] = [];
	if (!isWax) rows.push({ label: "Banded", value: yesNo(p.Banded) });
	if (!isSaw) rows.push({ label: "Grooved Timber", value: p.GroovedTimber });
	rows.push({ label: "Band Length", value: p.BandLength, fraction: true });
	if (isSaw || isGalvSawed)
		rows.push({ label: "# of Bands", value: p.NumberOfBands });
	if (isWax) rows.push({ label: "Cardboard", value: yesNo(p.Cardboard) });
	return rows;
};

// ── Row builders per area ──────────────────────────────────────────

const sawSpecRows = (p: Product): SpecRow[] => [
	{ label: "Target Length", value: p.TargetLength, fraction: true },
	{ label: "Top Tolerance", value: p.TopTolerance, fraction: true },
	{ label: "Bottom Tolerance", value: p.BottomTolerance, fraction: true },
	{ label: "Finish Pc/Bundle", value: p.FinishPcB },
	{ label: "Mill Length", value: p.MillLength },
	{ label: "Mill Pc/Bundle", value: p.MillPcB },
	...finishSpecRows(p, "saw"),
];

const weldSpecRows = (p: Product): SpecRow[] => [
	{ label: "Cut Length", value: p.CutLength, fraction: true },
	{ label: "Finished Length", value: p.FinishedLength, fraction: true },
	{ label: "Tolerance", value: p.Tolerance },
	{ label: "Program", value: p.Program },
	{ label: "Cleaning Cycle", value: p.CleaningCycle },
	{ label: "Test Cycle Interval", value: p.TestCycleInterval },
	{ label: "Web Holes", value: p.WebHoles },
	{ label: "Entry Keeper", value: p.EntryKeeperCode },
	{ label: "Exit Keeper", value: p.ExitKeeperCode },
	{ label: "Bottom Keeper", value: p.BottomKeeper },
	{ label: "Bundle Size", value: bundleSize(p) },
	{ label: "Stick Size", value: p.StickSize },
	{
		label: "Clip",
		value:
			p.ClipWidth || p.ClipHeight
				? `${p.ClipPartNumber ?? ""} (${p.ClipWidth} x ${p.ClipHeight})`
				: null,
	},
	{ label: "Comments", value: p.WeldComments },
	...finishSpecRows(p, "weld"),
];

const paintSpecRows = (p: Product): SpecRow[] => [
	{ label: "Cut Length", value: p.CutLength, fraction: true },
	{ label: "Finished Length", value: p.FinishedLength, fraction: true },
	{ label: "Web Holes", value: p.WebHoles },
	{ label: "Paint Cut Type", value: p.PaintCutTypeName },
	{ label: "Special Instructions", value: p.SpecialInstructions },
	{ label: "Bundle Size", value: bundleSize(p) },
	...finishSpecRows(p, "paint"),
];

const punchSpecRows = (p: Product): SpecRow[] => [
	{ label: "Cut Length", value: p.CutLength, fraction: true },
	{ label: "Finished Length", value: p.FinishedLength, fraction: true },
	{ label: "Bundle Size", value: bundleSize(p) },
	{ label: "Stick Size", value: p.StickSize },
	{ label: "Band Up", value: yesNo(p.BandUp) },
	{ label: "Upper Riser Drawing #", value: p.UpperRiserDrawingNum },
	{ label: "Lower Riser Drawing #", value: p.LowerRiserDrawingNum },
	{ label: "Stop Block #", value: p.StopBlockNumber },
	...finishSpecRows(p, "punch"),
];

const waxSpecRows = (p: Product): SpecRow[] => [
	{ label: "Bundle Size", value: bundleSize(p) },
	{ label: "Stick Size", value: p.StickSize },
	...finishSpecRows(p, "wax"),
];

const galvSawedSpecRows = (p: Product): SpecRow[] => [
	...finishSpecRows(p, "galv-sawed"),
];

const galvBundleSpecRows = (
	p: Product,
	area: "galv-welded" | "galv-punched",
): SpecRow[] => [
	{ label: "Bundle Size", value: bundleSize(p) },
	{ label: "Stick Size", value: p.StickSize },
	...finishSpecRows(p, area),
];

// ── Column defs ────────────────────────────────────────────────────

export const specColumnDefs: ColDef[] = [
	{
		field: "label",
		headerName: "",
		flex: 2,
		wrapText: true,
		autoHeight: true,
		cellStyle: { fontWeight: 600, color: "rgba(0,0,0,0.6)", textAlign: "left" },
		sortable: false,
		filter: false,
	},
	{
		field: "value",
		headerName: "",
		flex: 3,
		wrapText: true,
		autoHeight: true,
		cellStyle: { textAlign: "left" },
		sortable: false,
		filter: false,
	},
];

// ── Hook ───────────────────────────────────────────────────────────

type UseSpecTableDataArgs = {
	product: Product | null;
	hasSpecsForArea: boolean;
};

export const useSpecTableData = ({
	product,
	hasSpecsForArea,
}: UseSpecTableDataArgs) => {
	const specRows = useMemo(() => {
		if (!product || !hasSpecsForArea) return [];
		const area = getProductArea(product);
		switch (area) {
			case "saw":
				return sawSpecRows(product);
			case "weld":
				return weldSpecRows(product);
			case "paint":
				return paintSpecRows(product);
			case "punch":
				return punchSpecRows(product);
			case "wax":
				return waxSpecRows(product);
			case "galv-sawed":
				return galvSawedSpecRows(product);
			case "galv-welded":
			case "galv-punched":
				return galvBundleSpecRows(product, area);
			default:
				return [];
		}
	}, [product, hasSpecsForArea]);

	const allSpecRows = useMemo(
		() =>
			specRows.map((r) => ({
				label: r.label,
				value:
					r.value === null || r.value === undefined || r.value === ""
						? ""
						: r.fraction && typeof r.value === "number"
							? withFraction32(r.value)
							: String(r.value),
			})),
		[specRows],
	);

	return { columnDefs: specColumnDefs, rowData: allSpecRows };
};
